import type { ComponentChildren } from 'preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import type { ReleaseDoc } from '@kff/domain';
import { ApiError, createApi, type Api } from '../api.js';
import { ddm, ddmSv, dm, dmSv, when } from '../format.js';
import { LangContext, texts, useLang, type Lang } from '../i18n.js';
import { Callout, ConfirmDialog, Dialog, LoadError, Loading, MoreOptions, Pill, RowMenu, toast } from '../ui.js';
import { ProjectCasting, type BookedSlot } from './Casting.js';
import { ReleaseSigningDialog, type SigningState } from './signing.js';

/**
 * Actors on a project and their model releases (owner 2026-10-16, e-signing 2026-10-27). The producer adds actors – from
 * the shared register or new – with role, fee and shoot date, and sends the release for signing: the actor (or, as an
 * option, a parent or guardian) gets a personal link by email, or the producer opens it on their own phone at the shoot.
 * The signed PDF with the signing certificate goes to both. The crew see who is in the films and whether it is signed.
 */

type Status = 'draft' | 'sent' | 'signed' | 'cancelled';
export interface ActorRow {
  id: string;
  talentId: string;
  name: string;
  email: string | null;
  phone: string | null;
  birthDate: string | null;
  kinds: Kind[];
  hasPhoto: boolean;
  /** a parent or guardian signs (by the birth date, or ticked) */
  minor: boolean;
  role: string | null;
  fee: number | null;
  shootDate: string | null;
  usage: string | null;
  guardianName: string | null;
  guardianEmail: string | null;
  /** the reference the actor puts on the invoice, and the company they invoice through (0058) */
  invoiceRef: string;
  billingName: string | null;
  billingOrgNo: string | null;
  status: Status;
  statusText: string;
  sent: { at: string; by: string; expiresAt: string | null; version: number | null } | null;
  signed: { at: string; name: string | null; role: 'actor' | 'guardian' | null } | null;
  /** the latest e-signing, in short */
  signing: { status: 'open' | 'completed' | 'declined' | 'revoked' | 'expired'; partyStatus: 'waiting' | 'pending' | 'done' | 'declined' | null; sentAt: string; viewedAt: string | null; doneAt: string | null; comments: number } | null;
  missing: string[];
  /** said, but sending is not stopped: the shoot moved after the release was sent */
  warn?: string[];
}
/** What the crew see: who is in the films, and whether the release is signed */
export interface CrewActorRow {
  id: string;
  name: string;
  kinds: Kind[];
  role: string | null;
  shootDate: string | null;
  usage: string | null;
  released: boolean;
}
export type Kind = 'actor' | 'voice';
export const KIND_LABEL: Record<Kind, string> = { actor: 'Actor', voice: 'Voice-over' };
export const KIND_LABEL_SV: Record<Kind, string> = { actor: 'Skådespelare', voice: 'Voice-over' };
export const kindLabels = (lang: Lang = 'en'): Record<Kind, string> => (lang === 'sv' ? KIND_LABEL_SV : KIND_LABEL);
interface RegisterRow {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  birthDate: string | null;
  kinds: Kind[];
  gender: 'female' | 'male' | 'other' | null;
  ageRange: string | null;
  locations: string[];
  languages: string[];
  rating: number | null;
  /** the camera's ratings after shoots: average and how many */
  rated?: { avg: number; count: number } | null;
  hasPhoto: boolean;
  guardianName: string | null;
  guardianEmail: string | null;
  projects: number;
  onProject: boolean;
}
type ActorsView = { actors: ActorRow[]; register: RegisterRow[]; shoots?: ShootDay[]; usage?: string | null; canEdit: true } | { actors: CrewActorRow[]; canEdit: false };
type SendResult = SigningState & { onsite?: { token: string; url: string; session: string } };

/** Staff texts: the Actors tab, its dialogs and the release page around the (Swedish) release itself. */
const TX = texts(
  {
    status: { draft: 'Not sent', sent: 'Waiting for signature', signed: 'Signed', cancelled: 'Cancelled' } as Record<Status, string>,
    /** words the API sends in English, shown in the person's language */
    api: { guardian: 'Under 18 – add the parent or guardian who signs', moved: 'The shoot is no longer on this day – change the date and send the release again', former: 'Former colleague', someone: 'Someone' },
    // the add / edit dialog
    editTitle: (name: string) => `Edit ${name}`,
    addTitle: 'Add an actor or voice-over',
    cancel: 'Cancel',
    saving: 'Saving…',
    save: 'Save',
    addToProject: 'Add to project',
    savedNewLink: 'Saved – send it for signing again, the old link stopped working',
    saved: 'Saved',
    added: 'Added to the project',
    linkStopsOnSave: 'The signing link stops working when you save. Send it again afterwards.',
    who: 'Who',
    fromLibrary: 'From the library',
    someoneNew: 'Someone new',
    registerLegend: 'Actors and voice-over artists KFF works with',
    searchPlaceholder: 'Name, city, language, accent…',
    searchLabel: 'Search the library',
    type: 'Type',
    all: 'All',
    rating: (n: number) => `Rating ${n} of 5`,
    projects: (n: number) => `${n} ${n === 1 ? 'project' : 'projects'}`,
    noMatch: 'No one matches. Add them as someone new, or in the library under Actors.',
    name: 'Name',
    email: 'Email',
    phone: 'Phone',
    birthOptional: 'Birth date (optional)',
    worksAs: 'Works as',
    role: 'Role',
    roleHint: 'e.g. Customer at the till, or Narrator',
    shootDate: 'Shoot or recording date',
    noShootDate: 'No date yet',
    otherDate: 'Another date…',
    fee: 'Fee (kr excl. VAT)',
    feeHint: 'Leave empty if unpaid',
    usage: 'Limits on use (optional)',
    usageHint: 'Only if the material may not be used everywhere, e.g. “Only in organic social media for 12 months”.',
    byBirthYes: 'Yes – by the birth date, on the shoot date.',
    guardianOption: 'A parent or guardian signs (under 18)',
    guardianSigns: 'A parent or guardian signs',
    guardianSignsBody: 'They get the link and sign the release for the child.',
    guardianName: "Parent or guardian's name",
    guardianEmail: 'Their email',
    billingName: 'Invoices through (company)',
    billingOrgNo: 'Org.nr',
    billingHint: 'Only if the actor invoices through a company. The release asks them to mark the invoice with its reference; the Hub then finds the invoice in Bokio.',
    invoiceRef: (r: string) => `invoice ref ${r}`,
    billedVia: (c: string) => `invoices via ${c}`,
    billingTitle: (n: string) => `How ${n} invoices`,
    billingMenu: 'Invoices through…',
    // the signing link
    // the Actors tab
    shoot: (d: string) => `shoot ${d}`,
    noFee: 'no fee',
    under18By: (who: string) => `under 18, signed by ${who}`,
    guardianMissing: '(parent or guardian missing)',
    signedBy: (name: string | null, guardian: boolean, at: string) => `Signed${name ? ` by ${name}` : ''}${guardian ? ' (parent or guardian)' : ''} ${at}`,
    linkBy: (by: string, at: string) => `Link made by ${by} ${at}`,
    actors: 'Actors',
    nSigned: (signed: number, of: number) => `${signed} of ${of} signed`,
    addActor: 'Add actor or voice-over…',
    signedRelease: 'Signed release',
    previewRelease: 'Preview release',
    sendSigning: 'Send for signing',
    sendAgain: 'Send again',
    onsite: 'Sign on site',
    onsiteHint: 'Opens the release on this device to hand over at the shoot, without the code by email',
    signingBtn: 'Signing…',
    fixMissing: 'Fill in what is missing',
    changeDate: 'Change the date',
    rowMenu: (name: string) => `More for ${name}`,
    direct: 'Added without casting',
    addDirect: 'Add someone directly…',
    sentTo: (n: string) => `Sent to ${n} for signing`,
    sentLine: (at: string) => `Sent for signing ${at}`,
    openedIt: 'opened it',
    comments: (n: number) => `${n} ${n === 1 ? 'comment' : 'comments'}`,
    declinedLine: 'Declined to sign',
    expiredLine: 'The link ran out: send again',
    signedPdf: 'Signed release (PDF)',
    releaseOk: 'Release signed',
    releaseMissing: 'Release not signed',
    usageIs: (u: string) => `Use: ${u}`,
    crewHint: 'Who is in the films, and whether their model release is signed. The producer handles the releases.',
    crewEmpty: 'No actors on this project.',
    edit: 'Edit',
    remove: 'Remove',
    empty:
      'No actors yet. Add the people who appear or speak in the films – from the actor and voice-over library, or someone new – and send each one the model release to sign, by email or on your phone at the shoot.',
    templateNote: 'The release text is under Sales › Templates › Model release. Draft for legal review.',
    removeTitle: (name: string) => `Remove ${name}?`,
    removeBodySent: 'The signing link stops working. They stay in the library.',
    removeBody: 'They stay in the library.',
    // the release page
    loadingRelease: 'Loading the model release…',
    print: 'Print or save as PDF',
    back: 'Back to the project',
    waiting: 'Waiting for signature',
    preview: 'Preview',
    waitingBody: 'This is the release as it was sent.',
    previewBody: 'The details are filled in from the project when you send it for signing.',
    unknownTitle: 'Unknown fields in the template',
    unknownFix: ' – fix the template under Sales › Templates.'
  },
  {
    status: { draft: 'Inte skickat', sent: 'Väntar på signatur', signed: 'Signerat', cancelled: 'Avbrutet' },
    api: { guardian: 'Under 18 – lägg till föräldern eller vårdnadshavaren som skriver under', moved: 'Inspelningen är inte längre den här dagen – ändra datumet och skicka avtalet igen', former: 'Tidigare kollega', someone: 'Någon' },
    editTitle: (name: string) => `Ändra ${name}`,
    addTitle: 'Lägg till skådespelare eller voice-over',
    cancel: 'Avbryt',
    saving: 'Sparar…',
    save: 'Spara',
    addToProject: 'Lägg till i projektet',
    savedNewLink: 'Sparat – skicka för signering igen, den gamla länken slutade fungera',
    saved: 'Sparat',
    added: 'Tillagd i projektet',
    linkStopsOnSave: 'Signeringslänken slutar fungera när du sparar. Skicka igen efteråt.',
    who: 'Vem',
    fromLibrary: 'Från biblioteket',
    someoneNew: 'Någon ny',
    registerLegend: 'Skådespelare och voice-over-artister som KFF jobbar med',
    searchPlaceholder: 'Namn, ort, språk, dialekt…',
    searchLabel: 'Sök i biblioteket',
    type: 'Typ',
    all: 'Alla',
    rating: (n: number) => `Betyg ${n} av 5`,
    projects: (n: number) => `${n} projekt`,
    noMatch: 'Ingen matchar. Lägg till personen som någon ny, eller i biblioteket under Skådespelare.',
    name: 'Namn',
    email: 'E-post',
    phone: 'Telefon',
    birthOptional: 'Födelsedatum (valfritt)',
    worksAs: 'Jobbar som',
    role: 'Roll',
    roleHint: 't.ex. Kund i kassan, eller Berättare',
    shootDate: 'Inspelningsdatum',
    noShootDate: 'Inget datum än',
    otherDate: 'Annat datum…',
    fee: 'Arvode (kr exkl. moms)',
    feeHint: 'Lämna tomt om det inte är betalt',
    usage: 'Begränsningar i användningen (valfritt)',
    usageHint: 'Bara om materialet inte får användas överallt, t.ex. ”Bara i organiska sociala medier i 12 månader”.',
    byBirthYes: 'Ja – enligt födelsedatumet, på inspelningsdagen.',
    guardianOption: 'En förälder eller vårdnadshavare signerar (under 18)',
    guardianSigns: 'En förälder eller vårdnadshavare skriver under',
    guardianSignsBody: 'Hen får länken och skriver under avtalet för barnet.',
    guardianName: 'Förälderns eller vårdnadshavarens namn',
    guardianEmail: 'Hens e-post',
    billingName: 'Fakturerar via (bolag)',
    billingOrgNo: 'Org.nr',
    billingHint: 'Bara om skådespelaren fakturerar via ett bolag. Avtalet ber hen märka fakturan med referensen; då hittar Hubben fakturan i Bokio.',
    invoiceRef: (r: string) => `fakturaref ${r}`,
    billedVia: (c: string) => `fakturerar via ${c}`,
    billingTitle: (n: string) => `Så fakturerar ${n}`,
    billingMenu: 'Fakturerar via…',
    shoot: (d: string) => `inspelning ${d}`,
    noFee: 'inget arvode',
    under18By: (who: string) => `under 18, signeras av ${who}`,
    guardianMissing: '(förälder eller vårdnadshavare saknas)',
    signedBy: (name: string | null, guardian: boolean, at: string) => `Signerat${name ? ` av ${name}` : ''}${guardian ? ' (förälder eller vårdnadshavare)' : ''} ${at}`,
    linkBy: (by: string, at: string) => `Länk gjord av ${by} ${at}`,
    actors: 'Skådespelare',
    nSigned: (signed: number, of: number) => `${signed} av ${of} signerade`,
    addActor: 'Lägg till skådespelare eller voice-over…',
    signedRelease: 'Signerat modellavtal',
    previewRelease: 'Förhandsvisa modellavtalet',
    sendSigning: 'Skicka för signering',
    sendAgain: 'Skicka igen',
    onsite: 'Signera på plats',
    onsiteHint: 'Öppnar avtalet på den här enheten att lämna över vid inspelningen, utan kod via mejl',
    signingBtn: 'Signering…',
    fixMissing: 'Fyll i det som saknas',
    changeDate: 'Ändra datumet',
    rowMenu: (name: string) => `Mer för ${name}`,
    direct: 'Tillagda utan casting',
    addDirect: 'Lägg till någon direkt…',
    sentTo: (n: string) => `Skickat till ${n} för signering`,
    sentLine: (at: string) => `Skickat för signering ${at}`,
    openedIt: 'har öppnat',
    comments: (n: number) => `${n} ${n === 1 ? 'kommentar' : 'kommentarer'}`,
    declinedLine: 'Avböjde att signera',
    expiredLine: 'Länken gick ut: skicka igen',
    signedPdf: 'Signerat avtal (PDF)',
    releaseOk: 'Modellavtal klart',
    releaseMissing: 'Modellavtal saknas',
    usageIs: (u: string) => `Användning: ${u}`,
    crewHint: 'Vilka som medverkar i filmerna och om deras modellavtal är signerat. Producenten sköter avtalen.',
    crewEmpty: 'Inga skådespelare på projektet.',
    edit: 'Ändra',
    remove: 'Ta bort',
    empty:
      'Inga skådespelare än. Lägg till de som syns eller hörs i filmerna – från biblioteket med skådespelare och voice-over, eller någon ny – och skicka modellavtalet för signering till var och en, via mejl eller i din telefon vid inspelningen.',
    templateNote: 'Avtalstexten finns under Sälj › Mallar › Modellavtal. Utkast för juridisk granskning.',
    removeTitle: (name: string) => `Ta bort ${name}?`,
    removeBodySent: 'Signeringslänken slutar fungera. Personen finns kvar i biblioteket.',
    removeBody: 'Personen finns kvar i biblioteket.',
    loadingRelease: 'Laddar modellavtalet…',
    print: 'Skriv ut eller spara som PDF',
    back: 'Tillbaka till projektet',
    waiting: 'Väntar på signatur',
    preview: 'Förhandsvisning',
    waitingBody: 'Så här ser avtalet ut som skickades.',
    previewBody: 'Uppgifterna fylls i från projektet när du skickar för signering.',
    unknownTitle: 'Okända fält i mallen',
    unknownFix: ' – rätta mallen under Sälj › Mallar.'
  }
);

/** The API's English words (what stops sending, "Someone") in the person's language; anything else as it came. */
const fromApi = (s: string, lang: Lang) => {
  const k = (Object.keys(TX.en.api) as (keyof typeof TX.en.api)[]).find((x) => TX.en.api[x] === s);
  return k ? TX[lang].api[k] : s;
};

const TONE: Record<Status, 'ok' | 'info' | 'warn' | 'neutral'> = { draft: 'neutral', sent: 'info', signed: 'ok', cancelled: 'warn' };
const errText = (e: unknown) => (e instanceof Error ? e.message : String(e));
const val = (e: Event) => (e.target as HTMLInputElement | HTMLTextAreaElement).value;
const kr = (n: number) => `${n.toLocaleString('sv-SE')} kr`;
const day = (k: string, lang: Lang) => (lang === 'sv' ? dmSv(k) : dm(k));
const today = () => new Date().toLocaleDateString('sv-SE', { timeZone: 'Europe/Stockholm' });
const minorOn = (birth: string, on: string) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(birth)) return false;
  return `${Number(birth.slice(0, 4)) + 18}${birth.slice(4)}` > on;
};

/* ---------------- add or edit an actor ---------------- */

interface Form {
  name: string;
  email: string;
  phone: string;
  birthDate: string;
  kinds: Kind[];
  role: string;
  fee: string;
  shootDate: string;
  usage: string;
  /** a parent or guardian signs (when the birth date does not say) */
  minor: boolean;
  guardianName: string;
  guardianEmail: string;
  billingName: string;
  billingOrgNo: string;
}
const EMPTY: Form = { name: '', email: '', phone: '', birthDate: '', kinds: ['actor'], role: '', fee: '', shootDate: '', usage: '', minor: false, guardianName: '', guardianEmail: '', billingName: '', billingOrgNo: '' };
const formOf = (a: ActorRow): Form => ({
  name: a.name,
  email: a.email ?? '',
  phone: a.phone ?? '',
  birthDate: a.birthDate ?? '',
  kinds: a.kinds,
  role: a.role ?? '',
  fee: a.fee === null ? '' : String(a.fee),
  shootDate: a.shootDate ?? '',
  usage: a.usage ?? '',
  minor: a.minor,
  guardianName: a.guardianName ?? '',
  guardianEmail: a.guardianEmail ?? '',
  billingName: a.billingName ?? '',
  billingOrgNo: a.billingOrgNo ?? ''
});

/** "Actor · 25–40 · Stockholm · Swedish, English" */
export function talentLine(r: Pick<RegisterRow, 'kinds' | 'ageRange' | 'locations' | 'languages'> & { email?: string | null }, lang: Lang = 'en') {
  const kinds = kindLabels(lang);
  return [r.kinds.map((k) => kinds[k]).join(' & '), r.ageRange, r.locations.slice(0, 2).join(', ') || null, r.languages.slice(0, 3).join(', ') || null].filter(Boolean).join(' · ');
}

function Field(p: { label: string; hint?: string; value: string; onInput: (v: string) => void; type?: string; wide?: boolean; area?: boolean; autoComplete?: string }) {
  return (
    <label class="field">
      <span class="label">{p.label}</span>
      {p.hint ? <span class="hint">{p.hint}</span> : null}
      {p.area ? (
        <textarea class="textarea act-usage" value={p.value} maxLength={1000} onInput={(e) => p.onInput(val(e))} />
      ) : (
        <input class={`input${p.wide ? ' input--wide' : ''}`} type={p.type ?? 'text'} value={p.value} autoComplete={p.autoComplete ?? 'off'} onInput={(e) => p.onInput(val(e))} />
      )}
    </label>
  );
}

/** A shoot day the project already has (Inspelningar, or Monday's shoot day). */
export interface ShootDay {
  date: string;
  start: string | null;
  end: string | null;
  location: string | null;
}

const todayKey0 = () => new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Stockholm' }).format(new Date());

/** The shoot day to start from: the only one, else the next one from today, else the last. */
export const firstShoot = (shoots: ShootDay[]) => (shoots.length ? (shoots.find((s) => s.date >= todayKey0()) ?? shoots[shoots.length - 1]!).date : '');

const shootLabel = (s: ShootDay, lang: Lang) => [lang === 'sv' ? ddmSv(s.date) : ddm(s.date), s.start ? `${s.start}${s.end ? `–${s.end}` : ''}` : null, s.location].filter(Boolean).join(' · ');

/**
 * The shoot date, picked from the project's shoots (owner 2026-10-27: what the project knows is not typed again).
 * Without shoots, or for another day, a plain date.
 */
export function ShootDateField(p: { label: string; value: string; onChange: (v: string) => void; shoots: ShootDay[] }) {
  const lang = useLang();
  const t = TX[lang];
  const known = p.shoots.some((s) => s.date === p.value);
  const [other, setOther] = useState(!known && Boolean(p.value));
  if (!p.shoots.length) return <Field label={p.label} type="date" value={p.value} onInput={p.onChange} />;
  return (
    <label class="field">
      <span class="label">{p.label}</span>
      <select
        class="input"
        value={other ? '__other' : known ? p.value : ''}
        onChange={(e) => {
          const v = val(e);
          if (v === '__other') setOther(true);
          else {
            setOther(false);
            p.onChange(v);
          }
        }}
      >
        <option value="">{t.noShootDate}</option>
        {p.shoots.map((s) => (
          <option key={`${s.date}${s.start ?? ''}`} value={s.date}>
            {shootLabel(s, lang)}
          </option>
        ))}
        <option value="__other">{t.otherDate}</option>
      </select>
      {other ? <input class="input" type="date" value={p.value} aria-label={p.label} onInput={(e) => p.onChange(val(e))} /> : null}
    </label>
  );
}

function KindPick(p: { value: Kind[]; onChange: (k: Kind[]) => void }) {
  const lang = useLang();
  const kinds = kindLabels(lang);
  const flip = (k: Kind) => {
    const next = p.value.includes(k) ? p.value.filter((x) => x !== k) : [...p.value, k];
    if (next.length) p.onChange(next);
  };
  return (
    <fieldset class="fieldset">
      <legend class="label">{TX[lang].worksAs}</legend>
      <div class="cluster">
        {(['actor', 'voice'] as const).map((k) => (
          <label class="check" key={k}>
            <input type="checkbox" checked={p.value.includes(k)} onChange={() => flip(k)} />
            <span>{kinds[k]}</span>
          </label>
        ))}
      </div>
    </fieldset>
  );
}

function ActorDialog(p: { api: Api; id: string; register: RegisterRow[]; shoots: ShootDay[]; usage: string | null; edit: ActorRow | null; onDone: () => void; onClose: () => void }) {
  const lang = useLang();
  const t = TX[lang];
  const kinds = kindLabels(lang);
  // a new actor starts from the project's shoot day and the limits on use the others have
  const [f, setF] = useState<Form>(p.edit ? formOf(p.edit) : { ...EMPTY, shootDate: firstShoot(p.shoots), usage: p.usage ?? '' });
  const pickable = p.register.filter((r) => !r.onProject);
  const [mode, setMode] = useState<'register' | 'new'>(p.edit || !pickable.length ? 'new' : 'register');
  const [pick, setPick] = useState<string | null>(null);
  const [q, setQ] = useState('');
  const [kind, setKind] = useState<Kind | 'all'>('all');
  const [busy, setBusy] = useState(false);
  const set = (k: keyof Form) => (v: string) => setF({ ...f, [k]: v });
  const picked = pickable.find((r) => r.id === pick) ?? null;
  const birth = mode === 'register' ? (picked?.birthDate ?? '') : f.birthDate;
  // a known birth date decides; otherwise an adult signs unless the producer ticks that a parent or guardian does
  const byBirth = /^\d{4}-\d{2}-\d{2}$/.test(birth) ? minorOn(birth, f.shootDate || today()) : null;
  const minor = byBirth ?? f.minor;
  const needle = q.trim().toLocaleLowerCase('sv');
  const shown = pickable
    .filter((r) => kind === 'all' || r.kinds.includes(kind))
    .filter((r) => !needle || [r.name, r.email ?? '', r.phone ?? '', ...r.locations, ...r.languages, r.ageRange ?? ''].join(' ').toLocaleLowerCase('sv').includes(needle))
    .slice(0, 40);
  const choose = (r: RegisterRow) => {
    setPick(r.id);
    // the library remembers a child's parent or guardian
    if (r.guardianName && !f.guardianName) setF({ ...f, guardianName: r.guardianName, guardianEmail: r.guardianEmail ?? '' });
  };
  const save = async () => {
    setBusy(true);
    // a new actor left blank gets the company they invoiced through last time
    const billing = p.edit || f.billingName.trim() || f.billingOrgNo.trim() ? { billingName: f.billingName, billingOrgNo: f.billingOrgNo } : {};
    const casting = { role: f.role, fee: f.fee, shootDate: f.shootDate, usage: f.usage, minor, guardianName: minor ? f.guardianName : '', guardianEmail: minor ? f.guardianEmail : '', ...billing };
    const talent = { name: f.name, email: f.email, phone: f.phone, birthDate: f.birthDate, kinds: f.kinds };
    try {
      if (p.edit) await p.api.put(`/api/projects/${p.id}/actors/${p.edit.id}`, { ...casting, talent });
      else await p.api.post(`/api/projects/${p.id}/actors`, mode === 'register' ? { ...casting, talentId: pick } : { ...casting, talent });
      toast({ text: p.edit ? (p.edit.status === 'sent' ? t.savedNewLink : t.saved) : t.added });
      p.onDone();
    } catch (e) {
      toast({ text: errText(e) });
    } finally {
      setBusy(false);
    }
  };
  const ready = mode === 'register' ? Boolean(pick) : Boolean(f.name.trim());
  return (
    <Dialog
      title={p.edit ? t.editTitle(p.edit.name) : t.addTitle}
      onClose={p.onClose}
      foot={
        <>
          <button type="button" class="btn" onClick={p.onClose}>
            {t.cancel}
          </button>
          <button type="button" class="btn btn-primary" disabled={!ready || busy} onClick={() => void save()}>
            {busy ? t.saving : p.edit ? t.save : t.addToProject}
          </button>
        </>
      }
    >
      <div class="stack">
        {p.edit?.status === 'sent' ? <p class="hint">{t.linkStopsOnSave}</p> : null}
        {!p.edit && pickable.length ? (
          <div class="seg" role="group" aria-label={t.who}>
            <button type="button" class={`seg-btn${mode === 'register' ? ' is-on' : ''}`} aria-pressed={mode === 'register'} onClick={() => setMode('register')}>
              {t.fromLibrary}
            </button>
            <button type="button" class={`seg-btn${mode === 'new' ? ' is-on' : ''}`} aria-pressed={mode === 'new'} onClick={() => setMode('new')}>
              {t.someoneNew}
            </button>
          </div>
        ) : null}
        {mode === 'register' ? (
          <fieldset class="fieldset">
            <legend class="label">{t.registerLegend}</legend>
            <div class="cluster">
              <input class="input act-search" type="search" placeholder={t.searchPlaceholder} value={q} onInput={(e) => setQ(val(e))} aria-label={t.searchLabel} />
              <div class="seg seg--sm" role="group" aria-label={t.type}>
                {(['all', 'actor', 'voice'] as const).map((k) => (
                  <button key={k} type="button" class={`seg-btn${kind === k ? ' is-on' : ''}`} aria-pressed={kind === k} onClick={() => setKind(k)}>
                    {k === 'all' ? t.all : kinds[k]}
                  </button>
                ))}
              </div>
            </div>
            <div class="act-pick">
              {shown.map((r) => (
                <label class="check act-pick-row" key={r.id}>
                  <input type="radio" name="act-pick" checked={pick === r.id} onChange={() => choose(r)} />
                  <span>
                    <strong>{r.name}</strong>
                    <TalentStars x={r} />
                    <span class="muted small act-pick-sub">{[talentLine(r, lang), r.projects ? t.projects(r.projects) : null].filter(Boolean).join(' · ')}</span>
                  </span>
                </label>
              ))}
              {!shown.length ? <p class="muted small">{t.noMatch}</p> : null}
            </div>
          </fieldset>
        ) : (
          <div class="stack">
            <div class="act-grid">
              <Field label={t.name} value={f.name} onInput={set('name')} wide autoComplete="name" />
              <Field label={t.email} type="email" value={f.email} onInput={set('email')} wide />
              <Field label={t.phone} type="tel" value={f.phone} onInput={set('phone')} />
              <Field label={t.birthOptional} type="date" value={f.birthDate} onInput={set('birthDate')} />
            </div>
            <KindPick value={f.kinds} onChange={(kinds) => setF({ ...f, kinds })} />
          </div>
        )}
        <div class="act-grid">
          <Field label={t.role} hint={t.roleHint} value={f.role} onInput={set('role')} wide />
          <ShootDateField label={t.shootDate} value={f.shootDate} onChange={set('shootDate')} shoots={p.shoots} />
          <Field label={t.fee} hint={t.feeHint} value={f.fee} onInput={set('fee')} />
        </div>
        <Field label={t.usage} hint={t.usageHint} value={f.usage} onInput={set('usage')} area />
        {f.fee.trim() ? (
          <MoreOptions id="actor-billing" filled={f.billingName || f.billingOrgNo ? 1 : 0}>
            <p class="hint">{t.billingHint}</p>
            <div class="act-grid">
              <Field label={t.billingName} value={f.billingName} onInput={set('billingName')} wide />
              <Field label={t.billingOrgNo} value={f.billingOrgNo} onInput={set('billingOrgNo')} />
            </div>
          </MoreOptions>
        ) : null}
        {byBirth ? (
          <p class="small">{t.byBirthYes}</p>
        ) : byBirth === null ? (
          <MoreOptions id="actor-guardian" filled={f.minor ? 1 : 0} open={f.minor}>
            <label class="check">
              <input type="checkbox" checked={f.minor} onChange={() => setF({ ...f, minor: !f.minor })} />
              <span>{t.guardianOption}</span>
            </label>
          </MoreOptions>
        ) : null}
        {minor ? (
          <div class="stack">
            <Callout tone="info" title={t.guardianSigns}>
              {t.guardianSignsBody}
            </Callout>
            <div class="act-grid">
              <Field label={t.guardianName} value={f.guardianName} onInput={set('guardianName')} wide />
              <Field label={t.guardianEmail} type="email" value={f.guardianEmail} onInput={set('guardianEmail')} wide />
            </div>
          </div>
        ) : null}
      </div>
    </Dialog>
  );
}

/** Which company a signed actor invoices through: helps the Hub find the invoice, never changes the release. */
function BillingDialog(p: { api: Api; id: string; a: ActorRow; onClose: () => void; onDone: () => void }) {
  const t = TX[useLang()];
  const [name, setName] = useState(p.a.billingName ?? '');
  const [org, setOrg] = useState(p.a.billingOrgNo ?? '');
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await p.api.put(`/api/projects/${p.id}/actors/${p.a.id}/billing`, { billingName: name, billingOrgNo: org });
      toast({ text: t.saved });
      p.onDone();
    } catch (e) {
      toast({ text: errText(e) });
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog
      title={t.billingTitle(p.a.name)}
      sub={t.invoiceRef(p.a.invoiceRef)}
      onClose={p.onClose}
      foot={
        <>
          <button type="button" class="btn" onClick={p.onClose}>
            {t.cancel}
          </button>
          <button type="button" class="btn btn-primary" disabled={busy} onClick={() => void save()}>
            {t.save}
          </button>
        </>
      }
    >
      <p class="hint">{t.billingHint}</p>
      <div class="act-grid">
        <Field label={t.billingName} value={name} onInput={setName} wide />
        <Field label={t.billingOrgNo} value={org} onInput={setOrg} />
      </div>
    </Dialog>
  );
}

/* ---------------- the Actors tab ---------------- */

function line(a: ActorRow, lang: Lang) {
  const t = TX[lang];
  const kinds = kindLabels(lang);
  return [
    a.kinds.map((k) => kinds[k]).join(' & '),
    a.role,
    a.shootDate ? t.shoot(day(a.shootDate, lang)) : null,
    a.fee ? kr(a.fee) : t.noFee,
    a.fee ? t.invoiceRef(a.invoiceRef) : null,
    a.fee && a.billingName ? t.billedVia(a.billingName) : null,
    a.minor ? t.under18By(a.guardianName ?? t.guardianMissing) : null
  ]
    .filter(Boolean)
    .join(' · ');
}
function statusLine(a: ActorRow, lang: Lang) {
  const t = TX[lang];
  if (a.signed) return t.signedBy(a.signed.name, a.signed.role === 'guardian', when(a.signed.at, lang));
  const g = a.signing;
  if (g?.status === 'open')
    return [t.sentLine(when(g.sentAt, lang)), g.viewedAt ? t.openedIt : null, g.comments ? t.comments(g.comments) : null].filter(Boolean).join(' · ');
  if (g?.status === 'declined') return t.declinedLine;
  if (g?.status === 'expired') return t.expiredLine;
  if (a.sent && !g) return t.linkBy(fromApi(a.sent.by, lang), when(a.sent.at, lang));
  return null;
}

/** The crew's view: who is in the films, the role and the shoot, where the film may be used, and whether it is signed. */
function CrewActors(p: { actors: CrewActorRow[] }) {
  const lang = useLang();
  const t = TX[lang];
  const kinds = kindLabels(lang);
  return (
    <section class="blk" aria-labelledby="act-h">
      <div class="blk-head">
        <h2 class="blk-title" id="act-h">
          {t.actors}
        </h2>
      </div>
      <p class="small muted">{t.crewHint}</p>
      {p.actors.length ? (
        <ul class="act-list">
          {p.actors.map((a) => (
            <li key={a.id} class="act-row">
              <div class="act-main">
                <div>
                  <strong>{a.name}</strong> <Pill tone={a.released ? 'ok' : 'warn'}>{a.released ? t.releaseOk : t.releaseMissing}</Pill>
                </div>
                <div class="muted small">{[a.kinds.map((k) => kinds[k]).join(' & '), a.role, a.shootDate ? t.shoot(day(a.shootDate, lang)) : null].filter(Boolean).join(' · ')}</div>
                {a.usage ? <div class="small">{t.usageIs(a.usage)}</div> : null}
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <p class="muted">{t.crewEmpty}</p>
      )}
    </section>
  );
}

export function ProjectActors(p: { api: Api; id: string }) {
  const lang = useLang();
  const t = TX[lang];
  const [v, setV] = useState<ActorsView | null>(null);
  const [err, setErr] = useState<unknown>(null);
  const [dialog, setDialog] = useState<{ kind: 'add' } | { kind: 'edit' | 'remove' | 'billing'; a: ActorRow } | { kind: 'signing'; a: ActorRow; links?: SigningState['links'] } | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  // the actors booked through casting show on their role; the rest are listed on their own below
  const [cast, setCast] = useState<string[] | null>(null);
  const load = () => p.api.get<ActorsView>(`/api/projects/${p.id}/actors`).then(setV, setErr);
  useEffect(() => {
    void load();
  }, [p.id]);
  if (err) return <LoadError error={err} />;
  if (!v) return <Loading />;
  if (!v.canEdit) return <CrewActors actors={v.actors} />;
  const view = v;
  const signed = view.actors.filter((a) => a.status === 'signed').length;
  const base = (a: ActorRow) => `/api/projects/${p.id}/actors/${a.id}/signing`;
  const send = async (a: ActorRow, onsite: boolean) => {
    setBusy(a.id);
    try {
      const r = await p.api.post<SendResult>(base(a), onsite ? { onsite: true } : {});
      if (r.onsite) {
        // this device is the signer's for now: the page opens without the code (the log says it was on site)
        try {
          sessionStorage.setItem(`kff.esign.${r.onsite.token.slice(0, 36)}`, r.onsite.session);
        } catch {
          /* private mode: the page asks for the code instead */
        }
        location.assign(`/esign/${r.onsite.token}`);
        return;
      }
      const who = r.request?.parties.find((x) => x.role === 'signer');
      if (r.links?.length) setDialog({ kind: 'signing', a, links: r.links });
      else toast({ text: t.sentTo(who?.email ?? a.name) });
      void load();
    } catch (e) {
      toast({ text: errText(e) });
    } finally {
      setBusy(null);
    }
  };
  const signedPdf = async (a: ActorRow) => {
    try {
      const f = await p.api.get<{ name: string; mimeType: string; data: string }>(`${base(a)}/pdf?which=final`);
      const bytes = Uint8Array.from(atob(f.data), (ch) => ch.charCodeAt(0));
      window.open(URL.createObjectURL(new Blob([bytes], { type: f.mimeType })), '_blank', 'noopener');
    } catch (e) {
      toast({ text: errText(e) });
    }
  };
  /** One next step per actor (owner 2026-10-27: easy the first time); the rest is in the row's menu. */
  const slot = (a: ActorRow): BookedSlot => {
    const status = statusLine(a, lang);
    const open = a.signing?.status === 'open';
    const esigned = a.status === 'signed' && a.signing?.status === 'completed';
    const wait = busy === a.id;
    const release = `/projects/${p.id}/actors/${a.id}/release`;
    const primary = a.missing.length || a.warn?.length ? (
      <button type="button" class="btn btn-sm btn-primary" onClick={() => setDialog({ kind: 'edit', a })}>
        {a.missing.length ? t.fixMissing : t.changeDate}
      </button>
    ) : a.status === 'signed' ? (
      esigned ? (
        <button type="button" class="btn btn-sm" onClick={() => void signedPdf(a)}>
          {t.signedPdf}
        </button>
      ) : (
        <a class="btn btn-sm" href={release} target="_blank" rel="noopener">
          {t.signedRelease}
        </a>
      )
    ) : open ? (
      <button type="button" class="btn btn-sm btn-primary" onClick={() => setDialog({ kind: 'signing', a })}>
        {t.signingBtn}
      </button>
    ) : (
      <>
        <button type="button" class="btn btn-sm btn-primary" title={t.onsiteHint} disabled={wait} onClick={() => void send(a, true)}>
          {t.onsite}
        </button>
        <button type="button" class="btn btn-sm" disabled={wait} onClick={() => void send(a, false)}>
          {a.signing ? t.sendAgain : t.sendSigning}
        </button>
      </>
    );
    const items = [
      ...(a.status !== 'signed' || !esigned ? [{ label: a.status === 'signed' ? t.signedRelease : t.previewRelease, run: () => void window.open(release, '_blank', 'noopener') }] : []),
      ...(open && !a.missing.length ? [{ label: t.onsite, run: () => void send(a, true) }] : []),
      ...(a.signing && !open ? [{ label: t.signingBtn, run: () => setDialog({ kind: 'signing', a }) }] : []),
      // a signed release keeps its text, but which company invoices can still be told to the Hub
      ...(a.status === 'signed' && a.fee ? [{ label: t.billingMenu, run: () => setDialog({ kind: 'billing', a }) }] : []),
      ...(a.status !== 'signed' ? [{ label: t.edit, run: () => setDialog({ kind: 'edit', a }) }, { label: t.remove, run: () => setDialog({ kind: 'remove', a }), danger: true }] : [])
    ];
    return {
      status: t.status[a.status],
      tone: TONE[a.status],
      main: (
        <>
          <div class="muted small">{line(a, lang)}</div>
          {status ? <div class="small">{status}</div> : null}
          {[...a.missing, ...(a.warn ?? [])].map((m) => (
            <div key={m} class="small act-warn">
              {fromApi(m, lang)}
            </div>
          ))}
        </>
      ),
      actions: (
        <>
          {primary}
          <RowMenu label={t.rowMenu(a.name)} items={items} />
        </>
      )
    };
  };
  const byId = new Map(view.actors.map((a) => [a.id, a]));
  const direct = cast ? view.actors.filter((a) => !cast.includes(a.id)) : view.actors;
  return (
    <div class="stack">
      <ProjectCasting
        api={p.api}
        id={p.id}
        onBooked={() => void load()}
        booked={(cid) => {
          const a = byId.get(cid);
          return a ? slot(a) : null;
        }}
        onCastings={setCast}
        onAddDirect={() => setDialog({ kind: 'add' })}
        signed={signed}
        total={view.actors.length}
      />
      {direct.length || !cast ? (
        <section class="blk" aria-labelledby="act-h">
          <div class="blk-head">
            <h2 class="blk-title" id="act-h">
              {cast?.length ? t.direct : t.actors} {direct.length ? <Pill tone={direct.every((a) => a.status === 'signed') ? 'ok' : 'neutral'}>{t.nSigned(direct.filter((a) => a.status === 'signed').length, direct.length)}</Pill> : null}
            </h2>
            <div class="blk-actions">
              <button type="button" class="btn btn-sm" onClick={() => setDialog({ kind: 'add' })}>
                {t.addActor}
              </button>
            </div>
          </div>
          {direct.length ? (
            <ul class="act-list">
              {direct.map((a) => {
                const x = slot(a);
                return (
                  <li key={a.id} class="act-row">
                    <div class="act-main">
                      <div>
                        <strong>{a.name}</strong> <Pill tone={x.tone}>{x.status}</Pill>
                      </div>
                      {x.main}
                    </div>
                    <div class="cluster act-actions">{x.actions}</div>
                  </li>
                );
              })}
            </ul>
          ) : (
            <p class="muted">{t.empty}</p>
          )}
          <p class="small muted">{t.templateNote}</p>
        </section>
      ) : (
        <p class="small muted">{t.templateNote}</p>
      )}
      {dialog?.kind === 'add' || dialog?.kind === 'edit' ? (
        <ActorDialog
          api={p.api}
          id={p.id}
          register={view.register}
          shoots={view.shoots ?? []}
          usage={view.usage ?? null}
          edit={dialog.kind === 'edit' ? dialog.a : null}
          onClose={() => setDialog(null)}
          onDone={() => {
            setDialog(null);
            void load();
          }}
        />
      ) : null}
      {dialog?.kind === 'signing' ? (
        <ReleaseSigningDialog api={p.api} base={base(dialog.a)} name={dialog.a.name} links={dialog.links} onClose={() => setDialog(null)} onChange={() => void load()} />
      ) : null}
      {dialog?.kind === 'billing' ? <BillingDialog api={p.api} id={p.id} a={dialog.a} onClose={() => setDialog(null)} onDone={() => (setDialog(null), void load())} /> : null}
      {dialog?.kind === 'remove' ? (
        <ConfirmDialog
          title={t.removeTitle(dialog.a.name)}
          body={dialog.a.sent ? t.removeBodySent : t.removeBody}
          confirmLabel={t.remove}
          danger
          onClose={() => setDialog(null)}
          onConfirm={async () => {
            // a failure keeps the dialog open with the reason
            await p.api.del(`/api/projects/${p.id}/actors/${dialog.a.id}`);
            void load();
          }}
        />
      ) : null}
    </div>
  );
}

/* ---------------- the release itself (always Swedish) ---------------- */

interface Signed {
  name: string | null;
  role: 'actor' | 'guardian' | null;
  at: string;
  ip?: string | null;
  signature: string | null;
}

function ReleaseText(p: { doc: ReleaseDoc; signed: Signed | null }) {
  const { doc } = p;
  return (
    <section class="contract-part" lang="sv">
      <header class="quote-head">
        <div>
          <div class="quote-brand">Klara Färdiga Film</div>
          <h1 class="quote-title">{doc.title}</h1>
        </div>
      </header>
      {doc.intro.map((x) => (
        <p key={x}>{x}</p>
      ))}
      {doc.sections.map((s) => (
        <div key={s.heading} class="quote-sec">
          <h2>{s.heading}</h2>
          {s.paras.map((x, i) => (
            <p key={i}>{x}</p>
          ))}
        </div>
      ))}
      <div class="signatures">
        <div class="signature">
          <div class="small">{doc.signer.role === 'guardian' ? 'Förälder eller vårdnadshavare för Medverkande' : 'Medverkande'}</div>
          {p.signed?.signature ? <img class="sign-img" src={p.signed.signature} alt={`Signatur av ${p.signed.name ?? ''}`} /> : <div class="signature-line" />}
          <div>{p.signed?.name ?? doc.signer.name}</div>
          <div class="small muted">
            {p.signed
              ? `Signerat elektroniskt ${new Date(p.signed.at).toLocaleString('sv-SE', { timeZone: 'Europe/Stockholm' })}${p.signed.ip ? ` · IP ${p.signed.ip}` : ''}`
              : 'Datum'}
          </div>
        </div>
      </div>
      <p class="small muted">Mall version {doc.templateVersion}</p>
    </section>
  );
}

/** Staff: the release as it will read (preview) or as it was signed – print or save as PDF. */
export function ReleaseDocument(p: { api: Api; id: string; cid: string }) {
  const t = TX[useLang()];
  const [r, setR] = useState<{ doc: ReleaseDoc; status: Status; textHash: string | null; signed: Signed | null } | null>(null);
  const [error, setError] = useState<unknown>(null);
  useEffect(() => {
    p.api.get<typeof r>(`/api/projects/${p.id}/actors/${p.cid}/release`).then(setR, setError);
  }, [p.cid]);
  if (error) return <LoadError error={error} />;
  if (!r) return <Loading label={t.loadingRelease} />;
  return (
    <article class="quote-doc contract-doc">
      <div class="quote-tools stack">
        <div class="cluster">
          <button type="button" class="btn btn-primary" onClick={() => print()}>
            {t.print}
          </button>
          <a class="btn" href={`/projects/${p.id}/actors`}>
            {t.back}
          </a>
        </div>
        {r.status !== 'signed' ? (
          <Callout tone="info" title={r.status === 'sent' ? t.waiting : t.preview}>
            {r.status === 'sent' ? t.waitingBody : t.previewBody}
          </Callout>
        ) : null}
        {r.doc.unknown.length ? (
          <Callout tone="warn" title={t.unknownTitle}>
            {r.doc.unknown.map((x) => `{{${x}}}`).join(' · ')}
            {t.unknownFix}
          </Callout>
        ) : null}
      </div>
      <ReleaseText doc={r.doc} signed={r.signed} />
      {/* printed with the release, so it stays Swedish like the release */}
      {r.signed && r.textHash ? (
        <p class="small muted sign-hash" lang="sv">
          Kontrollsumma (SHA-256) av avtalstexten: {r.textHash}
        </p>
      ) : null}
    </article>
  );
}

/* ---------------- the public pages: signing here, the application form in Talent.tsx ---------------- */

const PUBLIC_LANG_KEY = 'kff.public.lang';

/**
 * The language of a public page (signing, applying), picked like the client portal's: Swedish unless the visitor
 * switches, and the switch is remembered on this device.
 */
export function usePublicLang(): [Lang, () => void] {
  const [lang, setLang] = useState<Lang>(() => {
    try {
      return localStorage.getItem(PUBLIC_LANG_KEY) === 'en' ? 'en' : 'sv';
    } catch {
      return 'sv';
    }
  });
  const flip = () => {
    const next: Lang = lang === 'sv' ? 'en' : 'sv';
    try {
      localStorage.setItem(PUBLIC_LANG_KEY, next);
    } catch {
      /* private mode – the switch still holds for this visit */
    }
    setLang(next);
  };
  return [lang, flip];
}

/** A public page's frame, like the portal's: the brand, a language switch, no staff chrome. */
export function PublicFrame(p: { lang: Lang; onLang: () => void; children: ComponentChildren }) {
  return (
    <LangContext.Provider value={p.lang}>
      <div class="portal" lang={p.lang}>
        <header class="portal-top">
          <span class="brand">
            <span class="brand-mark" aria-hidden="true">
              KFF
            </span>
            Klara Färdiga Film
          </span>
          <span class="portal-top-end">
            <button type="button" class="portal-lang" onClick={p.onLang} lang={p.lang === 'sv' ? 'en' : 'sv'}>
              {p.lang === 'sv' ? 'English' : 'Svenska'}
            </button>
          </span>
        </header>
        <main id="main" class="portal-main">
          {p.children}
        </main>
      </div>
    </LangContext.Provider>
  );
}

/**
 * The API answers the public pages in Swedish: the page's own words for the errors it knows – keyed "code" or, for an
 * error about one field, "code:field" – and the API's text otherwise.
 */
export function publicError(e: unknown, known: Record<string, string>, fallback: string): string {
  if (!(e instanceof ApiError)) return fallback;
  const field = (e.details as { field?: unknown } | null | undefined)?.field;
  return known[typeof field === 'string' ? `${e.code}:${field}` : e.code] ?? e.message;
}

const SIGN = texts(
  {
    docTitle: 'Model release – Klara Färdiga Film',
    badLink: 'The link does not work',
    loadFailed: 'Could not fetch the release. Check the connection and reload the page.',
    fetching: 'Fetching the release…',
    thanks: 'Thank you! The release is signed',
    keepCopy: 'Keep a copy by printing the page or saving it as PDF.',
    print: 'Print or save as PDF',
    readFirst: 'Read the release and sign at the bottom. The release is in Swedish.',
    sign: 'Sign',
    yourName: 'Your name',
    yourNameGuardian: 'Your name (parent or guardian)',
    yourSignature: 'Your signature',
    drawHint: 'Draw with your finger or the mouse.',
    padLabel: 'Draw your signature here',
    clear: 'Clear',
    padWiped: 'The screen changed size, so the signature was cleared. Draw it again.',
    accept: (guardian: boolean) => `I have read the release and accept it${guardian ? ' on behalf of the participant (Medverkande)' : ''}.`,
    signFailed: 'Could not sign. Check the connection and try again.',
    signing: 'Signing…',
    signBtn: 'Sign the release',
    needName: 'your name',
    needSignature: 'your signature',
    needAccept: 'the tick box',
    stillMissing: (xs: string[]) => `Still to fill in: ${xs.join(', ')}.`,
    weKeep: 'We keep your name, your signature, the time and your IP address with the release.',
    errors: {
      link_invalid: 'This link no longer works. Ask your producer at Klara Färdiga Film for a new link.',
      link_expired: 'The link has expired. Ask your producer at Klara Färdiga Film for a new link.',
      'required:name': 'Write your name.',
      'required:accept': 'Tick that you have read and accept the release.',
      'required:signature': 'Draw your signature in the box.',
      release_changed: 'The release has changed since you opened the page. Reload the page and read it again.'
    }
  },
  {
    docTitle: 'Modellavtal – Klara Färdiga Film',
    badLink: 'Länken fungerar inte',
    loadFailed: 'Det gick inte att hämta avtalet. Kontrollera anslutningen och ladda om sidan.',
    fetching: 'Hämtar avtalet…',
    thanks: 'Tack! Avtalet är signerat',
    keepCopy: 'Spara en kopia genom att skriva ut sidan eller spara den som PDF.',
    print: 'Skriv ut eller spara som PDF',
    readFirst: 'Läs igenom avtalet och signera längst ner.',
    sign: 'Signera',
    yourName: 'Ditt namn',
    yourNameGuardian: 'Ditt namn (förälder eller vårdnadshavare)',
    yourSignature: 'Din signatur',
    drawHint: 'Rita med fingret eller musen.',
    padLabel: 'Rita din signatur här',
    clear: 'Rensa',
    padWiped: 'Skärmen ändrade storlek, så signaturen rensades. Rita den igen.',
    accept: (guardian: boolean) => `Jag har läst avtalet och godkänner det${guardian ? ' för Medverkandes räkning' : ''}.`,
    signFailed: 'Det gick inte att signera. Kontrollera anslutningen och försök igen.',
    signing: 'Signerar…',
    signBtn: 'Signera avtalet',
    needName: 'ditt namn',
    needSignature: 'din signatur',
    needAccept: 'kryssrutan',
    stillMissing: (xs: string[]) => `Kvar att fylla i: ${xs.join(', ')}.`,
    weKeep: 'Vi sparar ditt namn, din signatur, tidpunkten och din IP-adress tillsammans med avtalet.',
    errors: {
      link_invalid: 'Länken fungerar inte längre. Be din producent på Klara Färdiga Film om en ny länk.',
      link_expired: 'Länken har gått ut. Be din producent på Klara Färdiga Film om en ny länk.',
      'required:name': 'Skriv ditt namn.',
      'required:accept': 'Kryssa i att du har läst och godkänner avtalet.',
      'required:signature': 'Rita din signatur i rutan.',
      release_changed: 'Avtalet har ändrats sedan du öppnade sidan. Ladda om sidan och läs igen.'
    }
  }
);

/**
 * A drawing area for the signature: mouse, pen or finger. The canvas follows its box (a phone turned, a window
 * resized); resizing a canvas wipes it, so the signature then counts as empty and the signer is told to draw it again.
 */
export function SignaturePad(p: { onChange: (png: string | null) => void }) {
  const t = SIGN[useLang()];
  const ref = useRef<HTMLCanvasElement>(null);
  const drawing = useRef(false);
  const inked = useRef(0);
  const onChange = useRef(p.onChange);
  onChange.current = p.onChange;
  const [wiped, setWiped] = useState(false);
  useEffect(() => {
    const c = ref.current!;
    let size = '';
    const fit = () => {
      const ratio = Math.max(1, window.devicePixelRatio || 1);
      const w = Math.round(c.offsetWidth * ratio);
      const h = Math.round(c.offsetHeight * ratio);
      // a phone's address bar showing or hiding resizes the window but not the pad: keep the drawing then
      if (`${w}x${h}` === size) return;
      const first = !size;
      size = `${w}x${h}`;
      c.width = w;
      c.height = h;
      const g = c.getContext('2d');
      if (g) {
        g.scale(ratio, ratio);
        g.lineWidth = 2.2;
        g.lineCap = 'round';
        g.lineJoin = 'round';
        g.strokeStyle = '#0b1f3a';
      }
      drawing.current = false;
      if (!first && inked.current) {
        inked.current = 0;
        setWiped(true);
        onChange.current(null);
      }
    };
    fit();
    window.addEventListener('resize', fit);
    window.addEventListener('orientationchange', fit);
    return () => {
      window.removeEventListener('resize', fit);
      window.removeEventListener('orientationchange', fit);
    };
  }, []);
  const pos = (e: PointerEvent) => {
    const r = ref.current!.getBoundingClientRect();
    return [e.clientX - r.left, e.clientY - r.top] as const;
  };
  const down = (e: PointerEvent) => {
    e.preventDefault();
    ref.current!.setPointerCapture(e.pointerId);
    drawing.current = true;
    setWiped(false);
    const g = ref.current!.getContext('2d');
    const [x, y] = pos(e);
    g?.beginPath();
    g?.moveTo(x, y);
  };
  const move = (e: PointerEvent) => {
    if (!drawing.current) return;
    const g = ref.current!.getContext('2d');
    const [x, y] = pos(e);
    g?.lineTo(x, y);
    g?.stroke();
    inked.current++;
  };
  const up = () => {
    if (!drawing.current) return;
    drawing.current = false;
    p.onChange(inked.current > 8 ? ref.current!.toDataURL('image/png') : null);
  };
  const clear = () => {
    const c = ref.current!;
    c.getContext('2d')?.clearRect(0, 0, c.width, c.height);
    inked.current = 0;
    setWiped(false);
    p.onChange(null);
  };
  return (
    <div class="sign-pad-wrap">
      <canvas ref={ref} class="sign-pad" aria-label={t.padLabel} onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerCancel={up} />
      {wiped ? (
        <span class="hint" role="status">
          {t.padWiped}
        </span>
      ) : null}
      <button type="button" class="btn btn-sm btn-ghost" onClick={clear}>
        {t.clear}
      </button>
    </div>
  );
}

interface SignView {
  doc: ReleaseDoc;
  textHash: string;
  status: 'waiting' | 'signed';
  signed: Signed | null;
}

export function SignPage(p: { token: string }) {
  const api = createApi(null);
  const [lang, flipLang] = usePublicLang();
  const t = SIGN[lang];
  const [v, setV] = useState<SignView | null>(null);
  const [err, setErr] = useState<unknown>(null);
  const [name, setName] = useState('');
  const [accept, setAccept] = useState(false);
  const [sig, setSig] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [formErr, setFormErr] = useState<unknown>(null);
  useEffect(() => {
    document.title = t.docTitle;
  }, [lang]);
  useEffect(() => {
    api.get<SignView>(`/api/sign/${encodeURIComponent(p.token)}`).then((x) => {
      setV(x);
      setName(x.doc.signer.name);
    }, setErr);
  }, [p.token]);
  const submit = async () => {
    setFormErr(null);
    setBusy(true);
    try {
      setV(await api.post<SignView>(`/api/sign/${encodeURIComponent(p.token)}`, { name, accept, signature: sig, textHash: v!.textHash }));
      scrollTo(0, 0);
    } catch (e) {
      setFormErr(e);
    } finally {
      setBusy(false);
    }
  };
  // what still stops signing, said next to the disabled button
  const missing = [name.trim().length < 2 && t.needName, !sig && t.needSignature, !accept && t.needAccept].filter((x): x is string => Boolean(x));
  const why = missing.length > 0 && !busy;
  return (
    <PublicFrame lang={lang} onLang={flipLang}>
      {err ? (
        <section class="portal-card">
          <h1 class="portal-h1">{t.badLink}</h1>
          <p>{publicError(err, t.errors, t.loadFailed)}</p>
        </section>
      ) : !v ? (
        <Loading label={t.fetching} />
      ) : (
        <article class="quote-doc contract-doc sign-doc">
          {v.status === 'signed' ? (
            <div class="quote-tools stack">
              <Callout tone="ok" title={t.thanks}>
                {t.keepCopy}
              </Callout>
              <div>
                <button type="button" class="btn" onClick={() => print()}>
                  {t.print}
                </button>
              </div>
            </div>
          ) : (
            <p class="quote-tools">{t.readFirst}</p>
          )}
          <ReleaseText doc={v.doc} signed={v.signed} />
          {v.status === 'waiting' ? (
            <form
              class="portal-card stack sign-form"
              onSubmit={(e) => {
                e.preventDefault();
                void submit();
              }}
            >
              <h2 class="portal-h1">{t.sign}</h2>
              <label class="field">
                <span class="label">{v.doc.signer.role === 'guardian' ? t.yourNameGuardian : t.yourName}</span>
                <input class="input input--wide" value={name} autoComplete="name" onInput={(e) => setName(val(e))} />
              </label>
              <div class="field">
                <span class="label">{t.yourSignature}</span>
                <span class="hint">{t.drawHint}</span>
                <SignaturePad onChange={setSig} />
              </div>
              <label class="check">
                <input type="checkbox" checked={accept} onChange={(e) => setAccept((e.target as HTMLInputElement).checked)} />
                <span>{t.accept(v.doc.signer.role === 'guardian')}</span>
              </label>
              {formErr ? (
                <p class="field-error" role="alert">
                  {publicError(formErr, t.errors, t.signFailed)}
                </p>
              ) : null}
              {why ? (
                <p id="sign-missing" class="small muted">
                  {t.stillMissing(missing)}
                </p>
              ) : null}
              <button type="submit" class="btn btn-primary" disabled={busy || missing.length > 0} aria-describedby={why ? 'sign-missing' : undefined}>
                {busy ? t.signing : t.signBtn}
              </button>
              <p class="small muted">{t.weKeep}</p>
            </form>
          ) : null}
        </article>
      )}
    </PublicFrame>
  );
}

/**
 * One rating for an actor, the same in the library and a project's actors (owner 2026-11-01): the camera's average
 * after shoots when there is one, else the rating set by hand.
 */
export function TalentStars(p: { x: { rating: number | null; rated?: { avg: number; count: number } | null } }) {
  const lang = useLang();
  const { rating, rated } = p.x;
  if (rated) {
    const avg = rated.avg.toLocaleString(lang === 'sv' ? 'sv-SE' : 'en-GB');
    const label =
      lang === 'sv'
        ? `Betyg ${avg} av 5 efter ${rated.count} ${rated.count === 1 ? 'inspelning' : 'inspelningar'}`
        : `Rated ${avg} of 5 after ${rated.count} ${rated.count === 1 ? 'shoot' : 'shoots'}`;
    return (
      <span class="act-stars" aria-label={label} title={label}>
        {' ★'}
        <span class="small"> {avg} ({rated.count})</span>
      </span>
    );
  }
  if (!rating) return null;
  const label = lang === 'sv' ? `Betyg ${rating} av 5` : `Rating ${rating} of 5`;
  return (
    <span class="act-stars" aria-label={label}>
      {' ★'.repeat(rating)}
    </span>
  );
}
